import { useQueryClient } from '@tanstack/react-query';
import {
  CreatePendingPaymentDto,
  CreatePendingPaymentRequest,
  ErrorMessage,
  InitiatePendingPaymentResponse,
  PayByAmountDigitalBillDto,
  PayCartDto,
} from '../../../generated-api';

import { useMutateCreatePendingPayment } from './usePendingPaymentApi';
import { useCallback } from 'react';

type InitiatePendingPaymentArgs = {
  locationId: string;
  amount: number;
  orderReference?: string;
  deviceId: string;
  paymentMethodId: string;
  payCartDto?: PayCartDto;
  payByAmountDigitalBillDto?: PayByAmountDigitalBillDto;
  askForTip: boolean;
  currency: string;
  onSuccess?: (response: InitiatePendingPaymentResponse) => void;
  onError?: (error?: ErrorMessage) => void;
};

const useCreatePendingPayment = () => {
  const { mutate: initiatePendingPayment, isPending } = useMutateCreatePendingPayment();
  const queryClient = useQueryClient();

  const createPendingPayment = useCallback(
    async (props: InitiatePendingPaymentArgs) => {
      const createPendingPaymentDto: CreatePendingPaymentDto = {
        amount: props.amount,
        locationId: props.locationId,
        paymentMethodId: props.paymentMethodId,
        deviceId: props.deviceId,
        orderReference: props.orderReference,
        payCartDto: props.payCartDto,
        payByAmountDigitalBillDto: props.payByAmountDigitalBillDto,
        askForTip: props.askForTip,
        currency: props.currency,
      };

      const createPendingPaymentRequest: CreatePendingPaymentRequest = {
        createPendingPaymentDto: createPendingPaymentDto,
        locationId: props.locationId,
        paymentMethodId: props.paymentMethodId,
      };

      initiatePendingPayment(createPendingPaymentRequest, {
        onSuccess: (data) => {
          if (props.onSuccess) {
            props.onSuccess(data);
          }
        },
        onError: (error) => {
          if (props.onError) {
            props.onError(error);
          }
        },
        onSettled: () =>
          queryClient.refetchQueries({
            queryKey: ['active-payments', props.locationId],
          }),
      });
    },
    [initiatePendingPayment, queryClient]
  );

  return {
    createPendingPayment,
    isWorking: isPending,
  };
};

export default useCreatePendingPayment;
